import React, { useState, useEffect, useRef, lazy, Suspense, useCallback } from "react";
import { useSearchParams } from 'react-router-dom';
import CustomerSearch from "../components/pos/CustomerSearch";
import ProductSearch from "../components/pos/ProductSearch";
import TransactionsList from "../components/pos/TransactionsList";
import TransactionDetails from "../components/pos/TransactionDetails";
import DraftsList from "../components/pos/DraftsList";
import CustomerModal from "../components/CustomerModal";
import PosHeader from "../components/pos/PosHeader";
import PosStats from "../components/pos/PosStats";
import CashPaymentModal from "../components/pos/CashPaymentModal";
import SplitPaymentModal from "../components/pos/SplitPaymentModal";
import ReportsTab from "../components/pos/ReportsTab";
import DiscountModal from "../components/pos/DiscountModal";
import DiscountCodeModal from "../components/pos/DiscountCodeModal";
import CouponPaymentModal from "../components/pos/CouponPaymentModal";
import CouponCheckModal from "../components/pos/CouponCheckModal";
import CustomerSelectModal from "../components/pos/CustomerSelectModal";
import ReceiptsTab from "../components/pos/ReceiptsTab";
import DocumentTypeSelector from "../components/pos/DocumentTypeSelector";
import PaymentSection from "../components/pos/PaymentSection";
import CartHeader from "../components/pos/CartHeader";
import CustomerSection from "../components/pos/CustomerSection";
import CartTable from "../components/pos/CartTable";
import StatsBar from "../components/pos/StatsBar";
import WarningsSection from "../components/pos/WarningsSection";
import { showBonusToastSummary } from "../components/pos/BonusToast";
import useBarcodeScanner from "../hooks/useBarcodeScanner";
import useFiscalPrinter from "../hooks/useFiscalPrinter";
import useDrukPrzerwany from "../hooks/useDrukPrzerwany";
import DrukPrzerwanyModal from "../components/pos/DrukPrzerwanyModal";
import useCustomerManagement from "../hooks/useCustomerManagement";
import useCashPayment from "../hooks/useCashPayment";
import useCouponPayment, { zbudujSplitZKuponow } from "../hooks/useCouponPayment";
import useSplitPayment from "../hooks/useSplitPayment";
import useShift from "../hooks/useShift";
import useReports from "../hooks/useReports";
import usePaymentProcessing from "../hooks/usePaymentProcessing";
import useDiscounts from "../hooks/useDiscounts";
import useCartOperations from "../hooks/useCartOperations";
import useDrafts from "../hooks/useDrafts";
import useOrderTransaction from "../hooks/useOrderTransaction";
import { useLocation } from "../contexts/LocationContext";
import { useCart } from "../contexts/CartContext";
import { usePermissions } from "../contexts/PermissionsContext";
import { useAuth } from "../contexts/AuthContext";
import { transactionService } from "../services/transactionService";
import { productService } from "../services/productService";
import priceLimitsService from "../services/priceLimitsService";
import api from '../services/api';

// Lazy load heavy modals
const CorrectionModal = lazy(() => import('../components/pos/CorrectionModal'));
const CustomerHistoryModal = lazy(() => import('../components/CustomerHistoryModal'));
const OpenShiftEnhancedModal = lazy(() => import('../components/shifts/OpenShiftEnhancedModal'));
const CloseShiftEnhancedModal = lazy(() => import('../components/shifts/CloseShiftEnhancedModal'));

const PosPage = () => {
  const { selectedLocation, locationId } = useLocation();
  const { hasPermission, loading: permissionsLoading, isAdmin } = usePermissions();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Uprawnienia POS
  const canAccessPos = hasPermission('pos', 'dostep', locationId);
  const canSell = hasPermission('pos', 'sprzedaz', locationId);
  const canReturn = hasPermission('pos', 'zwroty', locationId);
  const canGiveDiscounts = hasPermission('pos', 'rabaty', locationId);
  const canCancel = hasPermission('pos', 'anulowanie', locationId);
  const canPrintCopy = hasPermission('pos', 'paragon_kopia', locationId);

  // Ref do ochrony przed double-click na płatnościach
  const paymentProcessingRef = useRef(false);

  // Stan do blokowania UI podczas przetwarzania płatności
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);

  // Hook do zarządzania koszykiem (CartContext)
  const {
    cart, setCart,
    editingPrices, setEditingPrices,
    currentTransaction, setCurrentTransaction,
    appliedDiscounts, setAppliedDiscounts,
    cartDiscountTotal, setCartDiscountTotal,
    priceLimitWarnings, setPriceLimitWarnings,
    calculateCartTotal, getEffectiveDiscount, getFinalTotal, getTotalAmount,
    removeFromCart, updateQuantity, toggleDiscountExclusion,
    clearCart: clearCartContext, resetDiscounts
  } = useCart();

  const [priceLimitSettings, setPriceLimitSettings] = useState(null); // Ustawienia granicy ceny
  const [premiumProducts, setPremiumProducts] = useState([]);
  const [bonusRefreshKey, setBonusRefreshKey] = useState(0);
  const cartSnapshotRef = useRef([]);
  const [activeTab, setActiveTab] = useState("pos"); // pos, paragony
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("gotowka");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  
  // Używamy locationId z kontekstu zamiast hardkodowanej wartości
  const currentLocationId = locationId;

  // Hook do zarzadzania klientami
  const {
    selectedCustomer, setSelectedCustomer,
    customerSearchQuery, setCustomerSearchQuery,
    customerSuggestions, showCustomerSuggestions, setShowCustomerSuggestions,
    customerSearchLoading, showAddCustomerModal, setShowAddCustomerModal,
    showEditCustomerModal, setShowEditCustomerModal, editingCustomer, setEditingCustomer,
    showCustomerHistoryModal, setShowCustomerHistoryModal,
    loadAllCustomers, selectCustomer, handleNewCustomerAdded,
    handleEditSelectedCustomer, handleUpdateCustomer, removeCustomer
  } = useCustomerManagement();

  // Uzytkownik z kontekstu autoryzacji.
  // BEZ fallbacku na "admin": podszywanie sie pod admina, gdy kontekst jeszcze sie nie
  // zaladowal, powodowalo pobranie CUDZYCH rabatow (i mylace dane kasjera na paragonie).
  // Pusty string = "jeszcze nie wiemy" -> konsumenci (rabaty, zmiana) czekaja.
  const currentUser = user?.login || "";

  // Hook do zarzadzania rabatami
  const {
    availableDiscounts,
    showDiscountModal, setShowDiscountModal,
    discountLoading,
    showDiscountCodeModal, setShowDiscountCodeModal,
    pendingDiscountId, setPendingDiscountId,
    discountVerificationCode, setDiscountVerificationCode,
    discountCustomerCard, setDiscountCustomerCard,
    discountInfluencerName, setDiscountInfluencerName,
    discountCodeError, setDiscountCodeError,
    loadAvailableDiscounts,
    applyDiscount,
    handleDiscountCodeSubmit,
    removeDiscount
  } = useDiscounts({
    currentUser,
    locationId: currentLocationId,
    cart,
    currentTransaction,
    setCurrentTransaction,
    appliedDiscounts,
    setAppliedDiscounts,
    cartDiscountTotal,
    setCartDiscountTotal,
    setError
  });

  // Hooki do platnosci
  const {
    showCashPaymentModal, setShowCashPaymentModal,
    cashReceived, setCashReceived, cashChange, setCashChange,
    validateCashPayment, resetCashPaymentModal
  } = useCashPayment();

  const {
    showCouponPaymentModal, setShowCouponPaymentModal,
    couponCode, setCouponCode, couponData, setCouponData,
    couponValidating, validateCouponCode, resetCouponPaymentModal,
    // K5: wiele kart podarunkowych na jeden paragon
    coupons = [], addCoupon, removeCoupon, couponError = '', setCouponError
  } = useCouponPayment();

  const {
    showSplitPaymentModal, setShowSplitPaymentModal,
    splitPayments, splitPaymentError, setSplitPaymentError,
    resetSplitPayments, updateSplitPaymentAmount, updateSplitPaymentCouponCode,
    addCouponRow, removeCouponRow, checkCouponRow,
    validateSplitPayments
  } = useSplitPayment();

  // Hook do zarządzania zmianą
  const {
    currentShift, setCurrentShift,
    showOpenShiftModal, setShowOpenShiftModal,
    showCloseShiftModal, setShowCloseShiftModal,
    loadCurrentShift, openShift, closeShift,
    handleShiftOpened, handleShiftClosed
  } = useShift(currentUser, locationId);

  // Stan dla potwierdzenia pytania o aplikację
  const [appPromptConfirmed, setAppPromptConfirmed] = useState(false);

  // Stany dla filtrów produktów w koszyku
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [onlyAvailable, setOnlyAvailable] = useState(true);
  const [categories, setCategories] = useState([]);

  // Stany dla szybkich produktów (przyciski szybkiego dodawania)
  const [quickProducts, setQuickProducts] = useState([]);

  // Stan dla typu dokumentu sprzedaży: 'paragon', 'faktura_imienna', 'faktura_firma'
  const [documentType, setDocumentType] = useState('paragon');
  const [blockFakturaFirma, setBlockFakturaFirma] = useState(false);
  const [skipWz, setSkipWz] = useState(false);
  const [showCompanyInvoiceModal, setShowCompanyInvoiceModal] = useState(false);

  // Stany dla korekty transakcji
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionTransaction, setCorrectionTransaction] = useState(null);

  // Stan dla sprawdzania kuponu
  const [showCouponCheckModal, setShowCouponCheckModal] = useState(false);

  // Hook do raportów i statystyk
  const {
    stats, salesTarget,
    dailyClosureReports, reportsLoading,
    selectedReport, setSelectedReport,
    showClosureDetailsModal, setShowClosureDetailsModal,
    reportFilters, setReportFilters,
    loadDailyStats, loadDailyClosureReports,
    loadReportDetails, loadSalesTarget
  } = useReports(currentLocationId);

  // Hook do drukarki fiskalnej
  const {
    connected: fiscalPrinterConnected,
    loading: fiscalPrinterLoading,
    autoPrint: autoPrintReceipt,
    setAutoPrint: setAutoPrintReceipt,
    connect: connectFiscalPrinter,
    disconnect: disconnectFiscalPrinter,
    printReceipt: fiscalPrintReceipt,
    printFromTransactionList: rawHandleFiscalPrintFromList,
    isSupported: isFiscalPrinterSupported,
    getProtocol: getFiscalProtocol
  } = useFiscalPrinter();

  // Paragon przerwany restartem strony w trakcie druku (11.09, PAR/0155/09/2026/KAL):
  // gdy localStorage ma znacznik "druk w toku", a serwer nie ma potwierdzenia,
  // pytamy kasjera, czy paragon wyszedl z drukarki. Sprawdzamy raz, gdy znany
  // jest kasjer i ma dostep do POS (zadania ida z jego sesja).
  const {
    wpis: drukPrzerwany,
    zajety: drukPrzerwanyZajety,
    potwierdzWydruk: drukPrzerwanyTak,
    potwierdzBrakWydruku: drukPrzerwanyNie,
    odlozNaPozniej: drukPrzerwanyNieWiem,
  } = useDrukPrzerwany({
    gotowy: !!currentUser && !permissionsLoading && canAccessPos,
    poZapisie: () => setBonusRefreshKey(k => k + 1),
  });

  // Opakowane: po fiskalizacji z DOWOLNEGO miejsca (TransactionsList, widget, modal)
  // triggeruj refresh widgetu "Ostatnie paragony" + modala
  const handleFiscalPrintFromList = async (tx) => {
    try {
      return await rawHandleFiscalPrintFromList(tx);
    } finally {
      setBonusRefreshKey(k => k + 1);
    }
  };

  // Wrapper dla printFiscalReceipt (konwertuje stary interfejs na nowy z options)
  // WAŻNE: paymentMethodOverride jest przekazywane bezpośrednio z usePaymentProcessing
  // żeby uniknąć problemów z closure (stare wartości paymentMethod)
  const printFiscalReceipt = async (cartItems, totalAmount, cashierName, transactionId = null, customerNip = null, splitPaymentsData = null, documentNumber = null, paymentMethodOverride = null) => {
    const effectivePaymentMethod = paymentMethodOverride || paymentMethod;
    return fiscalPrintReceipt(cartItems, totalAmount, cashierName, {
      transactionId,
      customerNip,
      splitPaymentsData,
      documentNumber,
      paymentMethod: effectivePaymentMethod,
      appliedDiscounts
    });
  };

  // Pobierz premiowane produkty (dla oznaczeń ⭐ i toastów)
  const loadPremiumProducts = useCallback(async () => {
    try {
      const res = await api.get('/bonus/premium-products');
      if (res.data?.success && Array.isArray(res.data.data)) {
        setPremiumProducts(res.data.data);
      }
    } catch { /* ignore - bonus system may not be configured */ }
  }, []);

  // Callback po zakończeniu i ZAPISANIU transakcji (po sukcesie finalize + ew. fiskalizacji)
  const handleTransactionComplete = useCallback((cartItems) => {
    // [REFRESH-paragony] ZAWSZE odswiez liste/widget paragonow po zapisanej transakcji
    // (niezaleznie od produktow premiowanych) - trigger to inkrementowany licznik.
    // Wczesniej bump byl PO guardzie premiumProducts.length===0, wiec paragony bez
    // produktow premiowanych nie odswiezaly listy (efekt "off-by-one" / brak odswiezenia).
    setBonusRefreshKey(k => k + 1);

    // [L-doctype] Reset typu dokumentu i skipWz po sukcesie transakcji.
    // setState planuje jedynie re-render i NIE zmienia juz-zamknietych wartosci
    // (np. showSuccessMessage nadal uzyje poprawnego documentType z biezacego cyklu).
    setDocumentType('paragon');
    setSkipWz(false);

    // Bonus toast - tylko gdy w koszyku byly produkty premiowane
    if (!cartItems || cartItems.length === 0 || premiumProducts.length === 0) return;
    const premiumIds = new Set(premiumProducts.map(p => p.product_id));
    const premiumKategorieIds = new Set(premiumProducts.filter(p => p.kategoria_id).map(p => p.kategoria_id));
    let totalBonus = 0;
    let premCount = 0;
    cartItems.forEach(item => {
      const isPremium = premiumIds.has(item.id) || premiumKategorieIds.has(item.kategoria_id);
      if (isPremium) {
        const bp = premiumProducts.find(p => p.product_id === item.id || (p.kategoria_id && p.kategoria_id === item.kategoria_id));
        if (bp) {
          totalBonus += (bp.kwota_za_sztuke || 0) * (item.quantity || 1);
          premCount++;
        }
      }
    });
    if (totalBonus > 0) {
      showBonusToastSummary(totalBonus, premCount);
    }
  }, [premiumProducts]);

  // Hook do przetwarzania platnosci
  const { processPayment } = usePaymentProcessing({
    // Stan koszyka
    cart,
    getFinalTotal,
    getEffectiveDiscount,
    getTotalAmount,
    appliedDiscounts,
    clearCart: clearCartContext,
    setCurrentTransaction,
    currentTransaction,
    priceLimitSettings,
    priceLimitWarnings,
    // Stan platnosci
    paymentMethod,
    documentType,
    skipWz,
    // Stan zmiany
    currentShift,
    setShowCloseShiftModal,
    // Stan klienta
    selectedCustomer,
    // Stan lokalizacji
    currentLocationId,
    // Modale
    setShowCashPaymentModal,
    setCashReceived,
    setCashChange,
    setShowCouponPaymentModal,
    // Loading/error
    setLoading,
    setError,
    // Druk fiskalny
    autoPrintReceipt,
    printFiscalReceipt,
    // Statystyki
    loadDailyStats,
    loadSalesTarget,
    // Reset stanow po transakcji
    setEditingPrices,
    setAppliedDiscounts,
    setProductSearchQuery,
    setSelectedCategory,
    setSelectedCustomer,
    setCouponCode,
    resetCouponPaymentModal,
    resetSplitPayments,
    onTransactionComplete: handleTransactionComplete
  });

  // Hook do operacji na koszyku (addToCart, updatePrice)
  const { addToCart, updatePrice, validatePriceLimit } = useCartOperations({
    cart,
    setCart,
    currentShift,
    setShowCloseShiftModal,
    documentType,
    selectedCustomer,
    priceLimitSettings,
    setPriceLimitWarnings,
    currentLocationId
  });

  // Hook do szkicow (drafts)
  const { saveDraft, loadDraftToPOS } = useDrafts({
    cart,
    setCart,
    selectedCustomer,
    setSelectedCustomer,
    currentShift,
    currentLocationId,
    getFinalTotal,
    clearCart: clearCartContext,
    setActiveTab,
    setLoading,
    setError
  });

  // Hook do ladowania transakcji z zamowienia
  const { loadTransactionFromOrder } = useOrderTransaction({
    setCart,
    setCurrentTransaction,
    setAppliedDiscounts,
    setCartDiscountTotal,
    setSelectedCustomer,
    setActiveTab,
    setLoading,
    setError
  });

  // Hook do skanera kodow kreskowych
  // Wylacz skaner gdy jakikolwiek modal z polem do skanowania jest otwarty
  const anyModalWithScannerOpen = showDiscountCodeModal || showCouponPaymentModal || showCouponCheckModal || showSplitPaymentModal;
  const { searching: barcodeSearching, scannerEnabled: barcodeScannerEnabled, setScannerEnabled: setBarcodeScannerEnabled } = useBarcodeScanner({
    locationId,
    onProductFound: addToCart,
    enabled: !anyModalWithScannerOpen
  });

  useEffect(() => {
    loadInitialData();
    // Auto-połączenie z drukarką obsługiwane przez useFiscalPrinter hook
  }, []);

  // Pobierz ustawienie blokady faktury na firmę (admin) — ukrywa opcję w POS
  useEffect(() => {
    let mounted = true;
    api.get('/admin/invoice-settings')
      .then((res) => {
        if (!mounted) return;
        const blocked = !!res?.data?.data?.block_faktura_firma;
        setBlockFakturaFirma(blocked);
        if (blocked) setDocumentType((prev) => (prev === 'faktura_firma' ? 'paragon' : prev));
      })
      .catch(() => { /* brak ustawienia = domyślnie odblokowane */ });
    return () => { mounted = false; };
  }, []);

  // Obsługa parametrów URL - załaduj transakcję z zamówienia
  useEffect(() => {
    const transactionId = searchParams.get('transaction_id');
    const fromOrder = searchParams.get('from_order');
    
    if (transactionId) {
      loadTransactionFromOrder(transactionId);
      
      // Wyczyść parametry z URL, żeby nie ładować ponownie przy odświeżeniu
      setSearchParams({});
    }
  }, [searchParams]);

  // Reaguj na zmianę lokalizacji - przeładuj zmianę, cele sprzedaży i statystyki
  const prevLocationIdRef = useRef(locationId);
  useEffect(() => {
    if (locationId) {
      // Ceny specjalne i koszt FIFO pozycji sa per-lokalizacja. Przy REALNEJ
      // zmianie lokalizacji (nie przy pierwszym renderze) czyscimy koszyk, zeby
      // nie sprzedac po cenach/koszcie z poprzedniej lokalizacji.
      if (prevLocationIdRef.current != null &&
          prevLocationIdRef.current !== locationId &&
          cart && cart.length > 0) {
        clearCartContext();
        if (resetDiscounts) resetDiscounts();
      }
      prevLocationIdRef.current = locationId;

      loadCurrentShift();  // Przeładuj zmianę dla nowej lokalizacji
      loadSalesTarget();
      loadDailyStats();
    }
  }, [locationId]);

  // Wyczysc blad platnosci dzielonej przy zmianie metody platnosci
  useEffect(() => {
    setSplitPaymentError(null);
  }, [paymentMethod]);

  // Skaner kodow kreskowych - obslugiwany przez useBarcodeScanner hook

  // (loadPremiumProducts i handleTransactionComplete przeniesione przed usePaymentProcessing)

  const loadInitialData = async () => {
    try {
      // Załaduj aktualną zmianę
      await loadCurrentShift();

      // Załaduj statystyki dnia
      await loadDailyStats();

      // Załaduj cel sprzedaży
      await loadSalesTarget();

      // Rabaty NIE tutaj - laduje je useEffect reagujacy na uzytkownika i lokalizacje
      // (wczesniej byly pobierane dwa razy: stad i z useEffect ponizej).

      // Załaduj kategorie produktów
      await loadCategories();

      // Załaduj szybkie produkty
      await loadQuickProducts();

      // Załaduj premiowane produkty
      await loadPremiumProducts();
    } catch (err) {
      console.error("Blad ladowania danych:", err);
    }
  };

  // loadTransactionFromOrder - z useOrderTransaction hook

  // =============== FUNKCJE OBSLUGI RABATOW ===============

  const loadCategories = async () => {
    try {
      const response = await productService.getCategories();
      if (response.success) {
        setCategories(response.data);
      }
    } catch (err) {
      console.error("Błąd ładowania kategorii:", err);
    }
  };

  // =============== FUNKCJE SZYBKICH PRODUKTÓW ===============
  
  const loadQuickProducts = async () => {
    try {
      const response = await api.get('/pos/quick-products');
      const data = response.data;
      if (data.success) {
      setQuickProducts(data.data || []);
      }
    } catch (err) {
      console.error("Błąd ładowania szybkich produktów:", err);
    }
  };

  const handleQuickProductAdd = async (quickProduct) => {
    try {
      // Pobierz pełne dane produktu
      const response = await productService.getProduct(quickProduct.product_id, currentLocationId);
      if (response.success && response.data) {
        addToCart(response.data);
      } else {
        setError(`Nie można dodać produktu: ${quickProduct.nazwa}`);
      }
    } catch (err) {
      console.error("Błąd dodawania szybkiego produktu:", err);
      setError(`Błąd dodawania produktu: ${quickProduct.nazwa}`);
    }
  };

  // loadAvailableDiscounts, applyDiscount, handleDiscountCodeSubmit, removeDiscount - z useDiscounts hook

  // Załaduj rabaty gdy znany jest uzytkownik I lokalizacja.
  // Zaleznosc od samej funkcji (useCallback [currentUser, locationId]) - wczesniej
  // deps=[currentUser] gubily zmiane lokalizacji.
  useEffect(() => {
    loadAvailableDiscounts();
  }, [loadAvailableDiscounts]);

  // Załaduj rabaty przy otwarciu modala
  useEffect(() => {
    if (showDiscountModal) {
      loadAvailableDiscounts();
    }
  }, [showDiscountModal]);

  // =============== GRANICE CENY (MIN MARŻA) ===============

  // Załaduj ustawienia granic ceny
  useEffect(() => {
    const loadPriceLimitSettings = async () => {
      try {
        const response = await priceLimitsService.getSettings(currentLocationId);
        if (response.success && response.data) {
          // Konwertuj 1/0 z bazy na boolean
          const settings = {
            ...response.data,
            enabled: response.data.enabled === 1 || response.data.enabled === true,
            allow_override_with_permission: response.data.allow_override_with_permission === 1 || response.data.allow_override_with_permission === true
          };
          setPriceLimitSettings(settings);
        }
      } catch (error) {
        console.error("🚫 Błąd ładowania ustawień granic ceny:", error);
      }
    };
    loadPriceLimitSettings();
  }, [currentLocationId]);

  // =============== KONIEC FUNKCJI RABATÓW ===============

  const getInitialCustomerData = () => {
    if (!customerSearchQuery) return {};

    // Próbujemy określić czy to email, telefon czy nazwa
    const query = customerSearchQuery.trim();

    if (query.includes("@")) {
      return { email: query };
    } else if (/^\+?[\d\s\-\(\)]+$/.test(query)) {
      return { telefon: query };
    } else if (query.includes(" ")) {
      const parts = query.split(" ");
      return {
        imie: parts[0],
        nazwisko: parts.slice(1).join(" "),
      };
    } else {
      return { imie: query };
    }
  };

  const handleProductSelect = (product) => {
    addToCart(product);
  };

  const handleTransactionSelect = (transaction) => {
    setSelectedTransactionId(transaction.id);
    setSelectedTransaction(transaction);
  };

  // addToCart, updatePrice, validatePriceLimit - z useCartOperations

  // removeFromCart, toggleDiscountExclusion, updateQuantity - z CartContext

  // getTotalAmount - z CartContext

  const clearCart = () => {
    // Wyczyść koszyk i powiązane stany (CartContext)
    clearCartContext();
    // Resetuj pozostałe stany lokalne
    setSelectedCustomer(null);
    setError("");
    setDocumentType('paragon');
    resetSplitPayments();
    setShowSplitPaymentModal(false);
    setSplitPaymentError("");
    setAppPromptConfirmed(false);
    setCashReceived('');
    setCustomerSearchQuery('');
    setShowCustomerSuggestions(false);
    // Resetuj dane kuponu
    resetCouponPaymentModal();
  };

  // saveDraft - z useDrafts hook

  // Funkcja do obslugi platnosci gotowkowej
  const handleCashPayment = async () => {
    if (paymentProcessingRef.current || isPaymentProcessing) {
      console.log('Platnosc juz w trakcie, pomijam');
      return;
    }
    paymentProcessingRef.current = true;
    setIsPaymentProcessing(true);
    try {
      const finalAmount = getFinalTotal();
      const receivedAmount = parseFloat(cashReceived) || 0;

      if (receivedAmount < finalAmount) {
        setError(`Otrzymana kwota (${receivedAmount.toFixed(2)} zł) jest mniejsza od należności (${finalAmount.toFixed(2)} zł)`);
        return;
      }

      const change = receivedAmount - finalAmount;
      setCashChange(change);

      // Zamknij modal i wywołaj płatność
      setShowCashPaymentModal(false);
      await processPayment(receivedAmount);
    } finally {
      paymentProcessingRef.current = false;
      setIsPaymentProcessing(false);
    }
  };

  const handleCouponPayment = async () => {
    if (paymentProcessingRef.current || isPaymentProcessing) {
      console.log('Platnosc juz w trakcie, pomijam');
      return;
    }
    paymentProcessingRef.current = true;
    setIsPaymentProcessing(true);
    try {
      const validatedCoupon = await validateCouponCode(couponCode);
      if (validatedCoupon) {
        const finalTotal = getFinalTotal();
        const kod = String(validatedCoupon.code || couponCode || '').trim().toUpperCase();
        const saldo = Number(validatedCoupon.value ?? 0) || 0;

        // K5: pierwsza karta pokrywa calosc -> platnosc pojedynczym kuponem jak dotad
        if (coupons.length === 0 && saldo >= finalTotal) {
          setCouponData(validatedCoupon);
          setShowCouponPaymentModal(false);
          // Wyczyść kod kuponu od razu po użyciu
          setCouponCode('');
          // Kontynuuj z płatnością używając kuponu
          await processPayment(null, validatedCoupon);
          return;
        }

        // K5: karta nie pokrywa naleznosci (albo to kolejna karta) -> na liste kart.
        // Kazda karta raz - ta sama zeskanowana dwa razy nie zwieksza pokrycia.
        if (coupons.some(c => String(c.code || '').toUpperCase() === kod)) {
          if (setCouponError) setCouponError(`Ten sam kupon podano dwa razy (${kod}). Zeskanuj inną kartę.`);
          return;
        }
        if (saldo <= 0) {
          if (setCouponError) setCouponError(`Kupon ${kod} ma saldo 0,00 zł - nie pokryje żadnej części należności.`);
          return;
        }
        if (addCoupon) addCoupon({ ...validatedCoupon, code: kod, value: saldo });
        setCouponCode('');
        // Modal sam pokaze "Pozostaje X zl - dodaj kolejna karte" albo "Zaplac kuponami"
      }
    } catch (error) {
      if (setCouponError) setCouponError(`Błąd kuponu: ${error.message}`);
      setError(`Błąd kuponu: ${error.message}`);
    } finally {
      paymentProcessingRef.current = false;
      setIsPaymentProcessing(false);
    }
  };

  /**
   * K5: zaplata wieloma kartami podarunkowymi - pod spodem split_payments
   * z N wpisami 'kupon' (kwota = min(saldo, reszta), kolejnosc wpisania).
   * Ta sama sciezka serwera co platnosc dzielona.
   */
  const handlePayWithCoupons = async () => {
    if (paymentProcessingRef.current || isPaymentProcessing) {
      console.log('Platnosc juz w trakcie, pomijam');
      return;
    }
    paymentProcessingRef.current = true;
    setIsPaymentProcessing(true);
    try {
      const finalTotal = getFinalTotal();
      const { rows, pokryte, reszta } = zbudujSplitZKuponow(coupons, finalTotal);
      if (rows.length === 0 || reszta > 0) {
        const komunikat = `Karty pokrywają ${pokryte.toFixed(2)} zł z ${finalTotal.toFixed(2)} zł. Dodaj kolejną kartę albo użyj płatności dzielonej (tam też można podać kilka kart).`;
        if (setCouponError) setCouponError(komunikat);
        return;
      }
      setShowCouponPaymentModal(false);
      setCouponCode('');
      await processPayment(null, null, rows);
    } finally {
      paymentProcessingRef.current = false;
      setIsPaymentProcessing(false);
    }
  };

  const processSplitPayment = async () => {
    if (paymentProcessingRef.current || isPaymentProcessing) {
      console.log('Platnosc juz w trakcie, pomijam');
      return;
    }
    paymentProcessingRef.current = true;
    setIsPaymentProcessing(true);
    try {
      const totalAmount = getFinalTotal();

      // K5: suma co do grosza + KAZDY kupon osobno (kod, duplikat, saldo)
      // - jedna walidacja w hooku, komunikaty laduja w splitPaymentError.
      const wynik = await validateSplitPayments(totalAmount);
      if (!wynik || !wynik.valid) {
        return;
      }

      // Przetwórz płatność dzieloną
      setShowSplitPaymentModal(false);
      resetSplitPayments();
      await processPayment(null, null, splitPayments);
    } finally {
      paymentProcessingRef.current = false;
      setIsPaymentProcessing(false);
    }
  };

  // processPayment jest teraz z usePaymentProcessing hook

  // Wrapper dla processPayment z ochroną przed double-click
  const handleProcessPayment = async (receivedAmount = null, couponData = null, splitPaymentsData = null) => {
    if (paymentProcessingRef.current || isPaymentProcessing) {
      console.log('Platnosc juz w trakcie, pomijam');
      return;
    }
    paymentProcessingRef.current = true;
    setIsPaymentProcessing(true);
    try {
      await processPayment(receivedAmount, couponData, splitPaymentsData);
    } finally {
      paymentProcessingRef.current = false;
      setIsPaymentProcessing(false);
    }
  };

  const handleCorrectionClick = (transaction) => {
    // Sprawdź uprawnienia do zwrotów/korekt
    if (!canReturn) {
      setError("Brak uprawnień do wykonywania korekt/zwrotów");
      return;
    }
    setCorrectionTransaction(transaction);
    setShowCorrectionModal(true);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Resetuj wybrane transakcje/szkice przy zmianie głównej zakładki
    if (tab !== "paragony") {
      setSelectedTransactionId(null);
    }
  };

  // handleLoadDraftToPOS - z useDrafts hook (loadDraftToPOS)

  // Sprawdz czy uzytkownik ma dostep do modulu POS
  // Poczekaj na załadowanie uprawnień przed sprawdzeniem
  if (permissionsLoading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
          Ladowanie uprawnien...
        </div>
      </div>
    );
  }

  // Admin zawsze ma dostęp
  if (!canAccessPos && !isAdmin) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="alert warning" style={{ maxWidth: '500px', margin: '2rem auto', flexDirection: 'column' }}>
          <strong>Brak dostepu</strong>
          <p style={{ margin: '4px 0 0', fontSize: '13px' }}>
            Nie masz uprawnien do modulu POS / Kasa. Skontaktuj sie z administratorem.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="pos-page"
      style={{
        padding: "0.75rem",
        fontSize: "12px",
        maxWidth: "1200px",
        margin: "0 auto"
      }}
    >
      {/* Header */}
      <PosHeader
        currentShift={currentShift}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        fiscalPrinter={{
          connected: fiscalPrinterConnected,
          loading: fiscalPrinterLoading,
          connect: connectFiscalPrinter,
          disconnect: disconnectFiscalPrinter,
          isSupported: isFiscalPrinterSupported,
          getProtocol: getFiscalProtocol
        }}
        barcodeScannerEnabled={barcodeScannerEnabled}
        setBarcodeScannerEnabled={setBarcodeScannerEnabled}
        barcodeSearching={barcodeSearching}
        onOpenShift={openShift}
        onCloseShift={closeShift}
        onLoadReports={loadDailyClosureReports}
        loading={loading}
      />

      <WarningsSection
        currentLocationId={currentLocationId}
        currentShift={currentShift}
        onShowCloseShiftModal={() => setShowCloseShiftModal(true)}
      />

      <StatsBar
        stats={stats}
        salesTarget={salesTarget}
        currentShift={currentShift}
        loading={loading}
        onOpenShift={openShift}
        onCloseShift={closeShift}
        bonusRefreshTrigger={bonusRefreshKey}
        documentType={documentType}
        onDocumentTypeChange={activeTab === "pos" ? setDocumentType : undefined}
        blockFakturaFirma={blockFakturaFirma}
        locationId={currentLocationId}
        onClickRecentReceipt={fiscalPrinterConnected ? handleFiscalPrintFromList : undefined}
        userLogin={user?.login}
      />

      {/* Główna sekcja POS - kompaktowy styl */}
      {activeTab === "pos" && (
        <div>
          {/* Koszyk z wbudowaną sekcją klienta */}
          <div
            style={{
              backgroundColor: "var(--card-bg)",
              border: "1px solid var(--border)",
              borderRadius: "0.375rem",
              marginBottom: "0.75rem",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
            }}
          >
            <CartHeader
              cartLength={cart.length}
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              onlyAvailable={onlyAvailable}
              onOnlyAvailableChange={setOnlyAvailable}
              quickProducts={quickProducts}
              onQuickProductAdd={handleQuickProductAdd}
              onSaveDraft={saveDraft}
              onClearCart={clearCart}
              customerSection={
                <CustomerSection
                  selectedCustomer={selectedCustomer}
                  customerSearchQuery={customerSearchQuery}
                  showCustomerSuggestions={showCustomerSuggestions}
                  customerSuggestions={customerSuggestions}
                  customerSearchLoading={customerSearchLoading}
                  error={error}
                  onSearchQueryChange={setCustomerSearchQuery}
                  onSelectCustomer={selectCustomer}
                  onRemoveCustomer={() => {
                    setSelectedCustomer(null);
                    setCustomerSearchQuery("");
                  }}
                  onEditCustomer={handleEditSelectedCustomer}
                  onShowHistory={() => setShowCustomerHistoryModal(true)}
                  onShowAddModal={() => setShowAddCustomerModal(true)}
                  onLoadAllCustomers={loadAllCustomers}
                  onShowSuggestions={() => setShowCustomerSuggestions(true)}
                  onHideSuggestions={() => setShowCustomerSuggestions(false)}
                  onCheckCoupon={() => setShowCouponCheckModal(true)}
                />
              }
            />

            <CartTable
              cart={cart}
              appliedDiscounts={appliedDiscounts}
              priceLimitWarnings={priceLimitWarnings}
              editingPrices={editingPrices}
              setEditingPrices={setEditingPrices}
              productSearchQuery={productSearchQuery}
              setProductSearchQuery={setProductSearchQuery}
              selectedCategory={selectedCategory}
              onlyAvailable={onlyAvailable}
              currentLocationId={currentLocationId}
              updateQuantity={updateQuantity}
              updatePrice={updatePrice}
              removeFromCart={removeFromCart}
              toggleDiscountExclusion={toggleDiscountExclusion}
              handleProductSelect={handleProductSelect}
              getEffectiveDiscount={getEffectiveDiscount}
              premiumProducts={premiumProducts}
            />
          </div>

          {/* Sekcja płatności */}
          <PaymentSection
            totalAmount={getFinalTotal()}
            cart={cart}
            loading={loading}
            isPaymentProcessing={isPaymentProcessing}
            currentShift={currentShift}
            canSell={canSell}
            canGiveDiscounts={canGiveDiscounts}
            paymentMethod={paymentMethod}
            appPromptConfirmed={appPromptConfirmed}
            onPaymentMethodChange={setPaymentMethod}
            onShowSplitPayment={() => setShowSplitPaymentModal(true)}
            onShowDiscountModal={() => setShowDiscountModal(true)}
            onProcessPayment={handleProcessPayment}
            onSaveDraft={saveDraft}
            onClearCart={clearCart}
            onConfirmAppPrompt={() => setAppPromptConfirmed(true)}
            skipWz={skipWz}
            onToggleSkipWz={() => setSkipWz(prev => !prev)}
          />
        </div>
      )}

      {/* Sekcja Paragony */}
      {activeTab === "paragony" && (
        <ReceiptsTab
          refreshTrigger={bonusRefreshKey}
          currentShift={currentShift}
          currentLocationId={currentLocationId}
          selectedTransactionId={selectedTransactionId}
          selectedTransaction={selectedTransaction}
          onTransactionSelect={handleTransactionSelect}
          onCorrectionClick={handleCorrectionClick}
          onFiscalPrint={fiscalPrinterConnected ? handleFiscalPrintFromList : null}
          onCloseDetails={() => { setSelectedTransactionId(null); setSelectedTransaction(null); }}
          canPrintCopy={canPrintCopy}
          canReturn={canReturn}
        />
      )}

      {/* Sekcja Szkice (wersje robocze paragonow) */}
      {activeTab === "szkice" && (
        <DraftsList onLoadDraft={loadDraftToPOS} />
      )}

      {/* Sekcja Raporty zamkniec dnia */}
      {activeTab === "raporty" && (
        <ReportsTab
          dailyClosureReports={dailyClosureReports}
          reportsLoading={reportsLoading}
          reportFilters={reportFilters}
          setReportFilters={setReportFilters}
          onLoadReports={loadDailyClosureReports}
        />
      )}

      {/* Modal wyboru klienta */}
      <CustomerSelectModal
        show={showCustomerModal}
        selectedCustomer={selectedCustomer}
        onCustomerSelect={setSelectedCustomer}
        onClose={() => setShowCustomerModal(false)}
        onRemoveCustomer={() => setSelectedCustomer(null)}
      />

      {/* Modal rabatów */}
      <DiscountModal
        show={showDiscountModal}
        availableDiscounts={availableDiscounts}
        appliedDiscounts={appliedDiscounts}
        onApplyDiscount={applyDiscount}
        onRemoveDiscount={removeDiscount}
        onClose={() => setShowDiscountModal(false)}
      />

      {/* Modal otwierania zmiany */}
      <Suspense fallback={<div className="text-center p-4">Ładowanie...</div>}>
        {showOpenShiftModal && (
          <OpenShiftEnhancedModal
            isOpen={showOpenShiftModal}
            onClose={() => setShowOpenShiftModal(false)}
            onSuccess={handleShiftOpened}
            locationId={currentLocationId}
            locationName={selectedLocation?.nazwa}
            currentUser={currentUser}
          />
        )}
      </Suspense>

      {/* Modal zamykania zmiany */}
      <Suspense fallback={<div className="text-center p-4">Ładowanie...</div>}>
        {showCloseShiftModal && (
          <CloseShiftEnhancedModal
            isOpen={showCloseShiftModal}
            onClose={() => setShowCloseShiftModal(false)}
            onSuccess={handleShiftClosed}
            currentShift={currentShift}
            locationId={currentLocationId}
            locationName={selectedLocation?.nazwa}
          />
        )}
      </Suspense>

      {/* Modal korekty - tylko dla użytkowników z uprawnieniem do zwrotów */}
      <Suspense fallback={<div className="text-center p-4">Ładowanie...</div>}>
        {showCorrectionModal && canReturn && (
          <CorrectionModal
            isOpen={showCorrectionModal}
            onClose={() => setShowCorrectionModal(false)}
            transaction={correctionTransaction}
            cashier={currentUser}
          />
        )}
      </Suspense>

      {/* Modal kodu weryfikacyjnego / karty klienta dla rabatu */}
      <DiscountCodeModal
        show={showDiscountCodeModal}
        discount={availableDiscounts.find(d => d.id === pendingDiscountId)}
        discountVerificationCode={discountVerificationCode}
        discountCustomerCard={discountCustomerCard}
        discountInfluencerName={discountInfluencerName}
        discountCodeError={discountCodeError}
        onVerificationCodeChange={(value) => {
          setDiscountVerificationCode(value);
          setDiscountCodeError('');
        }}
        onCustomerCardChange={(value) => {
          setDiscountCustomerCard(value);
          setDiscountCodeError('');
        }}
        onInfluencerNameChange={(value) => {
          setDiscountInfluencerName(value);
          setDiscountCodeError('');
        }}
        onSubmit={handleDiscountCodeSubmit}
        onCancel={() => {
          setShowDiscountCodeModal(false);
          setPendingDiscountId(null);
        }}
      />

      {/* Modal płatności kuponem */}
      <CouponPaymentModal
        show={showCouponPaymentModal}
        totalAmount={getFinalTotal()}
        couponCode={couponCode}
        couponValidating={couponValidating}
        onCouponCodeChange={setCouponCode}
        onConfirm={handleCouponPayment}
        onCancel={resetCouponPaymentModal}
        coupons={coupons}
        onRemoveCoupon={removeCoupon}
        couponError={couponError}
        onPay={handlePayWithCoupons}
      />

      {/* Modal sprawdzania kuponu */}
      <CouponCheckModal
        show={showCouponCheckModal}
        onClose={() => setShowCouponCheckModal(false)}
      />

      {/* Paragon byl w trakcie druku, gdy strona kasy sie zrestartowala - pytanie kasjera */}
      <DrukPrzerwanyModal
        wpis={drukPrzerwany}
        zajety={drukPrzerwanyZajety}
        onTak={drukPrzerwanyTak}
        onNie={drukPrzerwanyNie}
        onNieWiem={drukPrzerwanyNieWiem}
      />

      {/* Modal platnosci gotowkowej */}
      <CashPaymentModal
        show={showCashPaymentModal}
        totalAmount={getFinalTotal()}
        cashReceived={cashReceived}
        setCashReceived={setCashReceived}
        cashChange={cashChange}
        setCashChange={setCashChange}
        onConfirm={handleCashPayment}
        onCancel={resetCashPaymentModal}
      />

      {/* Modal platnosci dzielonej */}
      <SplitPaymentModal
        show={showSplitPaymentModal}
        totalAmount={getFinalTotal()}
        splitPayments={splitPayments}
        splitPaymentError={splitPaymentError}
        onUpdateAmount={updateSplitPaymentAmount}
        onUpdateCouponCode={updateSplitPaymentCouponCode}
        onAddCouponRow={addCouponRow}
        onRemoveCouponRow={removeCouponRow}
        onCheckCouponRow={checkCouponRow}
        onConfirm={processSplitPayment}
        onCancel={() => {
          setShowSplitPaymentModal(false);
          resetSplitPayments();
        }}
      />

      {/* Modal dodawania klienta */}
      {showAddCustomerModal && (
        <CustomerModal
          isOpen={showAddCustomerModal}
          onClose={() => setShowAddCustomerModal(false)}
          onSuccess={handleNewCustomerAdded}
          initialData={{
            imie: customerSearchQuery || '',
            telefon: ''
          }}
        />
      )}

      {/* Modal edycji klienta */}
      {showEditCustomerModal && editingCustomer && (
        <CustomerModal
          isEdit={true}
          customer={editingCustomer}
          setCustomer={setEditingCustomer}
          onSubmit={handleUpdateCustomer}
          onClose={() => {
            setShowEditCustomerModal(false);
            setEditingCustomer(null);
          }}
        />
      )}

      {/* Modal historii klienta */}
      <Suspense fallback={<div className="text-center p-4">Ładowanie...</div>}>
        <CustomerHistoryModal
          customerId={selectedCustomer?.id}
          customerName={
            selectedCustomer?.nazwa_firmy ||
            selectedCustomer?.name ||
            `${selectedCustomer?.imie || ''} ${selectedCustomer?.nazwisko || ''}`.trim() ||
            `Klient #${selectedCustomer?.id}`
          }
          isOpen={showCustomerHistoryModal}
          onClose={() => setShowCustomerHistoryModal(false)}
        />
      </Suspense>
    </div>
  );
};

export default PosPage;
